import { useLingui } from '@lingui/react';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import CocoModal from 'components/coco-modal/coco-modal';
import EmailIcon from '@mui/icons-material/Email';
import React, { useMemo, useState } from 'react';
import { msg, Trans } from '@lingui/macro';
import MutliEmailInput, {
  sendInvites,
} from 'components/multi-email-input/MultiEmailInput';
import { LoadingButton } from '@mui/lab';
import { useCommunityDetails } from 'atoms/community-details';
import { Link } from '@mui/icons-material';
import { ShareLinkPage } from 'components/add-members-modal/AddMembersLoginModal';
import { EMAIL_REGEX } from 'utils/validation';

export default function AddMembersEmailModal({
  onClose,
  communityId,
  onSubmit,
  onCancel,
}: {
  onClose: () => void;
  onCancel?: () => void;
  communityId: string;
  onSubmit?: () => void;
}) {
  const { _ } = useLingui();
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<string>('MEMBER');
  const [dispatching, setDispatching] = useState(false);
  const { community, loadCommunityDetails } = useCommunityDetails({
    communityId,
  });

  const [shareLinkVisible, setShareLinkVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const hasAtleastOneEmail = useMemo(() => {
    return emails.length > 0 || EMAIL_REGEX.test(inputValue);
  }, [emails, inputValue]);

  return (
    <CocoModal
      onClose={onClose}
      header={
        <Stack direction='row' alignItems='center'>
          {shareLinkVisible ? (
            <>
              <EmailIcon style={{ width: '2rem' }} />
              <Typography color='white'>
                <Trans>Email Invites Sent</Trans>
              </Typography>
            </>
          ) : (
            <>
              <EmailIcon style={{ width: '2rem' }} />
              <Typography color='white'>
                <Trans>Invite by Email</Trans>
              </Typography>
            </>
          )}
        </Stack>
      }
    >
      {shareLinkVisible ? (
        <ShareLinkPage communityId={communityId} isEmailInvite />
      ) : (
        <Stack minWidth='400px' padding={3}>
          <Typography>
            <Trans>
              Type an email and press enter to add more, or copy / paste
              multiple emails.
              <br />
            </Trans>
          </Typography>
          <Typography my={1} variant='body2'>
            <Trans>
              Note: If inviting anyone <strong>under the age of 13</strong>,
              please enter their parent/guardian email.
            </Trans>
          </Typography>
          <MutliEmailInput
            emails={emails}
            setEmails={setEmails}
            hidePlaceholder
            setRoles={setRole}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
          <Stack mt={4} direction='row' justifyContent='flex-start' spacing={2}>
            <Button
              onClick={() => {
                onCancel?.();
                onClose();
              }}
              variant='outlined'
            >
              <Trans>Cancel</Trans>
            </Button>
            <Tooltip
              title={
                emails.length === 0
                  ? _(
                      msg`Please type in at least one email in the box above and press enter to add the email.`,
                    )
                  : ''
              }
            >
              <LoadingButton
                disabled={!hasAtleastOneEmail}
                loading={dispatching}
                onClick={async () => {
                  let addedEmails = emails?.length > 0 ? emails : [inputValue];
                  if (onSubmit) onSubmit();
                  setDispatching(true);
                  await sendInvites(communityId, addedEmails, [
                    role || 'MEMBER',
                  ]);
                  await loadCommunityDetails();
                  setDispatching(false);
                  setShareLinkVisible(true);
                }}
                variant='contained'
              >
                <Trans>Send Invite</Trans>
              </LoadingButton>
            </Tooltip>
          </Stack>
        </Stack>
      )}
    </CocoModal>
  );
}
